import React from "react";
import { useTranslation } from "react-i18next";

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";

import styles from "./styles.module.scss";

interface ApplyScopeConfirmModalProps {
  isOpen: boolean;
  onNewOnly: () => void;
  onAllUpcoming: () => void;
  onCancel: () => void;
}

// Shown after Save when a booking option's duration/location changed, so the
// coach chooses whether the change also applies to already-booked upcoming
// lessons. Each choice is a self-describing option; the footnote spells out what
// is never affected.
export default function ApplyScopeConfirmModal({
  isOpen,
  onNewOnly,
  onAllUpcoming,
  onCancel,
}: ApplyScopeConfirmModalProps) {
  const { t } = useTranslation();

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onCancel}
      classNames={styles.modal}
      wrapperClassName="confirm_cancel"
    >
      <div className={styles.content}>
        <div className={styles.icon}>
          <i className="ico ico-pencil-o" />
        </div>

        <h2 className={styles.title}>{t("Update Booked Sessions?")}</h2>
        <p className={styles.subtitle}>
          {t("You changed duration or location. Should this apply to sessions that were already booked?")}
        </p>

        <div className={styles.options}>
          <button
            type="button"
            className={styles.option}
            onClick={onAllUpcoming}
            data-testid="apply-scope-all-upcoming"
          >
            <span className={styles.optionTitle}>{t("All Upcoming Sessions")}</span>
            <span className={styles.optionDesc}>
              {t("Update already booked sessions to the new duration or location.")}
            </span>
          </button>

          <button
            type="button"
            className={styles.option}
            onClick={onNewOnly}
            data-testid="apply-scope-new-only"
          >
            <span className={styles.optionTitle}>{t("New Bookings Only")}</span>
            <span className={styles.optionDesc}>
              {t("Keep existing bookings as they are. Apply changes to new bookings only.")}
            </span>
          </button>
        </div>

        <p className={styles.footnote}>
          {t("Pricing, past sessions and athlete notifications are never affected.")}
        </p>

        <button
          type="button"
          className={styles.cancel}
          onClick={onCancel}
          data-testid="apply-scope-cancel"
        >
          {t("Cancel")}
        </button>
      </div>
    </ModalLayout>
  );
}
